import { useState, useEffect, ChangeEvent } from 'react';
import { Card, Col, Row, Toast, ToastContainer } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DatePicker from 'components/base/DatePicker';
import {
    faFileExport,
    faFilter,
    faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import LeadsFilterModal from 'components/modals/LeadsFilterModal';
import Badge, { BadgeBg } from 'components/base/Badge';


const getBadgeBg = (label: string): BadgeBg => {
    switch (label) {
        case 'done':
            return 'success';

        /*
        case 'canceled':
            return 'secondary';

        case 'in progress':
        case 'warm':
            return 'info';

        case 'hot':
            return 'danger';
        */

        default:
            return 'primary';
    }
};

export interface Feedback {
    feedback_id: number;
    feedback_type: string;
    feedback_title: string;
    feedback_from: string;
    feedback_to: string;
    job_role: string;
    notes: string;
    inserted_at: string;
    status: string;
}

const FeedbacksTable = () => {
    return (
        <div className="border-top">
            <AdvanceTable
                tableProps={{ className: 'phoenix-table fs-9' }}
                rowClassName="hover-actions-trigger btn-reveal-trigger"
            />
            <AdvanceTableFooter pagination className="py-4" />
        </div>
    );
};


const GivenFeedbacks = () => {
    const { t } = useTranslation();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },

        {
            label: t('Feedback'),
            url: '/feedback-dashboard'
        },
        {
            label: 'Given Feedbacks',
            active: true
        }
    ];


    const auth = useAuth();
    const navigate = useNavigate();
    const handleClick = () => {
        // relative path
        navigate('add/given');
    };

    const [loading, setLoading] = useState(true);

    const [openFilterModal, setOpenFilterModal] = useState(false);
    const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        table.setGlobalFilter(e.target.value || undefined);
    };

    const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
    //const [isUploading, setIsUploading] = useState(false);

    // State to control Toast messages
    const [show, setShow] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const conversationListTableColumns: ColumnDef<Feedback>[] = [
        {
            header: 'Title',
            accessorKey: 'feedback_title',
            cell: ({ row: { original } }) => {
                const { feedback_id, feedback_title } = original;
                return (
                    <Link to={`/feedback-dashboard/given-feedbacks/edit/${feedback_id}`} className="fw-semi-bold">
                        {feedback_title}
                    </Link>
                );
            },
            meta: {
                cellProps: { className: 'white-space-nowrap py-4' },
                headerProps: { style: { width: '25%' } }
            }
        },

        {
            header: 'Role',
            accessorKey: 'job_role',
            meta: {
                headerProps: { style: { width: '10%' }, className: 'ps-3' }
            }
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row: { original } }) => (
                <Badge variant="phoenix" bg={getBadgeBg(original.status)}>
                    {original.status}
                </Badge>
            ),
            meta: {
                headerProps: { style: { minWidth: 100, width: '10%' } }
            }
        },
        {
            header: t('GivenFeedbacks.table.feedback_to'),
            accessorKey: 'feedback_to',
            meta: {
                headerProps: { style: { width: '10%' }, className: 'ps-3' }
            }
        },

        {
            header: 'Date',
            accessorKey: 'inserted_at',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '12%' }, className: 'ps-3' }
            }
        },


    ];

    const table = useAdvanceTable({
        data: feedbacks,
        columns: conversationListTableColumns,
        pageSize: 6,
        pagination: true,
        sortable: true
    });


    const fetchData = async () => {
        try {
            const response = await apiClient.get('/v1/feedbacks/given');
            setFeedbacks(response.data);
        } catch (error) {
            const message = handleAPIError(error);
            if (message) {
                setToastMessage(message);
                setShow(true);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);



    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div>

            <PageBreadcrumb items={breadcrumbItems} />


            <ToastContainer className="p-3" position='top-center'>
                <Toast onClose={() => setShow(false)} show={show} delay={3000} autohide>
                    <Toast.Body className=''>{toastMessage}</Toast.Body>
                </Toast>
            </ToastContainer>

            <div className="mt-3">
                <Card className='border border-light rounded-5' >
                    <Card.Body>
                        <AdvanceTableProvider {...table}>
                            <Row className="g-3 justify-content-between mb-4">
                                <Col xs="auto">
                                    <Button
                                        variant="primary"
                                        className="me-4"
                                        startIcon={
                                            <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                                        }
                                        onClick={handleClick}
                                    >
                                        {t('GivenFeedbacks.add_feedback')}
                                    </Button>

                                    <Button
                                        variant="link"
                                        className="text-900 px-0"
                                        startIcon={
                                            <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                                        }
                                    >
                                        {t("Feedback.export")}
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <div className="d-flex">
                                        <SearchBox
                                            placeholder="Search by name"
                                            className="me-2"
                                            onChange={handleSearchInputChange}
                                        />
                                        <DatePicker defaultValue="Mar 1, 2022" />
                                        <Button
                                            variant="phoenix-secondary"
                                            className="px-3 ms-2"
                                            onClick={() => setOpenFilterModal(true)}
                                        >
                                            <FontAwesomeIcon
                                                icon={faFilter}
                                                transform="down-3"
                                                className="text-primary"
                                            />
                                        </Button>
                                    </div>
                                    <LeadsFilterModal
                                        show={openFilterModal}
                                        handleClose={() => setOpenFilterModal(false)}
                                    />
                                </Col>
                            </Row>
                            <FeedbacksTable />
                        </AdvanceTableProvider>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );


};

export default GivenFeedbacks;
